/**
 * DPoP proof construction for embed sessions — SER-5887.
 *
 * PURE by design: everything here takes a `CryptoKey` and returns a string, with
 * no storage, no window and no network. That is not stylistic — `indexedDB` is
 * `undefined` under the Bun + happy-dom test runtime while `crypto.subtle` is
 * fully present, so splitting the key STORE (embed-dpop-key.ts) from the proof
 * BUILDER is what makes the security-critical half directly testable.
 *
 * What this defends against: the embed bearer lives in sessionStorage, which is
 * trivially copied. A copied bearer works from any browser until it expires, and
 * the one-active-session slot does not catch it — copying gives two browsers ONE
 * session id, so nothing races for the slot. Signing each request with a key the
 * browser will not export is what makes the copy useless.
 *
 * Wire format is RFC 9449: a compact JWS, `typ: dpop+jwt`, ES256, carrying the
 * public JWK in its header. hodor recomputes the RFC 7638 thumbprint from that
 * JWK and compares it with the one it stored at exchange.
 */

/** The HTTP header carrying the proof. Must match hodor's `dpop.Header`. */
export const DPOP_HEADER = 'DPoP'

/** WebCrypto parameters. P-256 only — hodor pins the same curve. */
export const DPOP_KEY_ALGORITHM: EcKeyGenParams = {
  name: 'ECDSA',
  namedCurve: 'P-256',
}

const SIGN_ALGORITHM: EcdsaParams = { name: 'ECDSA', hash: { name: 'SHA-256' } }

/** base64url WITHOUT padding — the only encoding JOSE accepts. */
const base64Url = (bytes: ArrayBuffer | Uint8Array): string => {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let binary = ''
  for (const byte of view) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * UTF-8 bytes as a plain ArrayBuffer.
 *
 * `TextEncoder.encode` returns `Uint8Array<ArrayBufferLike>`, which TS 5.7+ will
 * not accept as WebCrypto's `BufferSource`. Sliced rather than cast so the value
 * is genuinely an ArrayBuffer at runtime too, and the byteOffset is honoured
 * instead of assumed to be zero.
 */
const utf8 = (value: string): ArrayBuffer => {
  const bytes = new TextEncoder().encode(value)
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

const encodeJson = (value: unknown): string => base64Url(utf8(JSON.stringify(value)))

/**
 * base64url(SHA-256(bearer)) — the `ath` claim.
 *
 * This is the claim that actually binds a proof to ONE session. `htm`/`htu` only
 * stop a captured proof being replayed against a different endpoint; without
 * `ath` a proof minted for session A would authorise session B.
 */
export const accessTokenHash = async (bearer: string): Promise<string> =>
  base64Url(await crypto.subtle.digest('SHA-256', utf8(bearer)))

/**
 * The public half of the key, in the exact shape hodor's thumbprint expects.
 *
 * `exportKey('jwk')` works on a PUBLIC key even when the private half is
 * non-extractable — that is the whole point of an asymmetric pair, and it is why
 * the keypair can be generated with `extractable: false` and still be usable.
 *
 * Only the four RFC 7638 members are kept. `exportKey` also returns `key_ops`,
 * `ext` and `alg`, and passing those through would not break the thumbprint
 * (hodor reads only the four) but would put needless bytes in every request.
 */
export const publicJwk = async (publicKey: CryptoKey): Promise<Record<string, string>> => {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey)
  if (!jwk.x || !jwk.y) {
    throw new Error('embed dpop: exported JWK has no coordinates')
  }
  return { crv: 'P-256', kty: 'EC', x: jwk.x, y: jwk.y }
}

/**
 * The RFC 7638 JWK thumbprint — the `jkt`.
 *
 * SHA-256 over a canonical JSON object holding ONLY the required members, in
 * lexicographic order, with no whitespace. This must agree with hodor's
 * `ecJWK.thumbprint()` (dpop/dpop.go) BYTE FOR BYTE: it is what the partner
 * sends as `dpop_jkt` at mint, what hodor stores in the token's `cnf.jkt`, and
 * what it compares the exchange proof's key against. A one-byte disagreement
 * does not degrade — it 400s every exchange, inside a customer's iframe.
 *
 * Two details make the agreement exact rather than probable:
 *
 * - ORDER. `crv < kty < x < y` is already lexicographic, and JS guarantees
 *   insertion order for non-integer string keys, so `JSON.stringify` over this
 *   literal emits the members in the order RFC 7638 requires. hodor hand-builds
 *   the same string with fmt.Sprintf because Go's map ordering is randomised;
 *   the two arrive at the same bytes from opposite directions.
 * - ESCAPING. Go's `%q` and `JSON.stringify` agree on every value that can
 *   appear here: `crv`/`kty` are the fixed literals, and `x`/`y` are base64url,
 *   so the alphabet is `[A-Za-z0-9-_]` and nothing needs escaping in either.
 *
 * Takes the JWK rather than the key so it stays pure and is testable against a
 * fixed vector with no key generation.
 */
export const jwkThumbprint = async (jwk: Record<string, string>): Promise<string> => {
  const canonical = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y })
  return base64Url(await crypto.subtle.digest('SHA-256', utf8(canonical)))
}

/** The thumbprint of a keypair's public half — what `getToken` is handed. */
export const publicKeyThumbprint = async (publicKey: CryptoKey): Promise<string> =>
  jwkThumbprint(await publicJwk(publicKey))

/**
 * WebCrypto returns an ECDSA signature as the raw r‖s concatenation, which is
 * exactly what JOSE's ES256 wants — no DER unwrapping needed. Noted because the
 * Node `crypto` API returns DER instead, and mixing the two produces signatures
 * that verify nowhere.
 */
export interface ProofInput {
  /** HTTP method, becomes `htm`. */
  method: string
  /**
   * Request PATH ONLY, becomes `htu`.
   *
   * Not the full URL: requests traverse CloudFront and an ALB, which rewrite
   * scheme and host, so a full-URL binding would fail in production for reasons
   * that have nothing to do with security. hodor compares against its own
   * request path.
   */
  path: string
  /**
   * The session bearer this proof is for; hashed into `ath`.
   *
   * OMITTED at exchange, deliberately. `ath` binds a proof to an access token,
   * and at exchange there is no token yet — hodor generates the bearer during
   * that very call, so any value the client put here could never match. RFC 9449
   * leaves ath out of the token request for the same reason, and hodor skips the
   * check when it has no bearer to compare against.
   */
  bearer?: string
}

/**
 * Build a signed DPoP proof for one request.
 *
 * A fresh `jti` and `iat` per call: proofs are single-use by intent, and hodor
 * bounds `iat` to a skew window so a captured one is short-lived.
 */
export const buildDpopProof = async (
  keyPair: CryptoKeyPair,
  { method, path, bearer }: ProofInput,
): Promise<string> => {
  const header = {
    alg: 'ES256',
    typ: 'dpop+jwt',
    jwk: await publicJwk(keyPair.publicKey),
  }
  const payload: Record<string, string | number> = {
    htm: method.toUpperCase(),
    htu: path,
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID(),
  }
  // Present only when there is a token to bind to — see ProofInput.bearer.
  if (bearer) {
    payload.ath = await accessTokenHash(bearer)
  }
  const signingInput = `${encodeJson(header)}.${encodeJson(payload)}`
  const signature = await crypto.subtle.sign(SIGN_ALGORITHM, keyPair.privateKey, utf8(signingInput))
  return `${signingInput}.${base64Url(signature)}`
}

/**
 * The path portion of a request, for `htu`.
 *
 * Handles both shapes the service layer produces: an absolute URL (most calls,
 * where baseURL carries the host) and a bare path (a few call sites pass one
 * with a host-only baseURL). A URL that will not parse falls back to the input
 * with any query stripped — `htu` excludes the query string by spec, and a
 * mismatch there is a silent 401.
 */
export const dpopPath = (url: string): string => {
  try {
    return new URL(url).pathname
  } catch {
    return url.split('?')[0]
  }
}
